import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Shield, ShieldCheck, ShieldAlert, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

/**
 * Settings → Security → Trusted Devices (iter124 bug #2).
 *
 * Lists every device the current user has previously signed in from.
 * The user can:
 *   • Star a device → marks it trusted; future sign-ins from that
 *     device don't fire the "new device sign-in" email.
 *   • Untrust → future sign-ins from that device DO fire the alert
 *     (essentially: "keep the record but flag if it comes back").
 *   • Revoke → destroys the device row AND kills every active session
 *     that came from that fingerprint. Useful for "sign out my phone
 *     from over there in one tap".
 */
export const TrustedDevices = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/auth/devices');
      setDevices(r.data?.devices || []);
    } catch (e) {
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleTrust = async (dev) => {
    setBusyId(dev.device_id);
    try {
      const r = await api.patch(`/auth/devices/${dev.device_id}/trust`);
      toast.success(r.data?.is_trusted ? 'Device trusted' : 'Device un-trusted');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (dev) => {
    if (dev.is_current) {
      toast.info('You can\'t revoke your current device. Sign out instead.');
      return;
    }
    if (!window.confirm(`Revoke "${dev.device_name || 'this device'}"? Any active sessions from it will be signed out immediately.`)) return;
    setBusyId(dev.device_id);
    try {
      const r = await api.delete(`/auth/devices/${dev.device_id}`);
      const killed = r.data?.sessions_killed || 0;
      toast.success(killed > 0 ? `Device revoked · ${killed} session(s) signed out` : 'Device revoked');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    } finally {
      setBusyId(null);
    }
  };

  const relTime = (iso) => {
    if (!iso) return '—';
    const then = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.floor((now - then) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)} min ago`;
    if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
    return `${Math.floor(s / 86400)} d ago`;
  };

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="trusted-devices-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-slate-900">
          <ShieldCheck className="w-5 h-5 text-blue-600" />
          Trusted Devices
        </CardTitle>
        <CardDescription className="text-slate-500">
          Star the devices you own so unfamiliar sign-ins stand out. Revoke any device you no longer use — active sessions from it are signed out immediately.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : devices.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">
            No devices tracked yet. They'll appear here after your next sign-in.
          </p>
        ) : (
          <div className="space-y-2">
            {devices.map((d) => (
              <div
                key={d.device_id}
                className={`flex items-center justify-between gap-3 p-3 border rounded-lg ${
                  d.is_current ? 'border-blue-200 bg-blue-50/40' : 'border-slate-200 bg-slate-50/60'
                }`}
                data-testid={`device-row-${d.device_id}`}
              >
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className={`mt-0.5 p-2 rounded ${d.is_trusted ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {d.is_trusted ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-slate-900 truncate">
                        {d.device_name || 'Unknown device'}
                      </span>
                      {d.is_current && (
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] px-1.5 py-0">
                          This device
                        </Badge>
                      )}
                      {d.is_trusted && (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] px-1.5 py-0">
                          Trusted
                        </Badge>
                      )}
                      {(d.active_sessions || 0) > 0 && !d.is_current && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] px-1.5 py-0">
                          {d.active_sessions} active
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      Last seen {relTime(d.last_used_at)} · IP {d.ip_address || 'unknown'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant={d.is_trusted ? 'ghost' : 'outline'}
                    size="sm"
                    disabled={busyId === d.device_id}
                    onClick={() => toggleTrust(d)}
                    className={d.is_trusted ? 'text-emerald-700' : 'text-slate-600'}
                    data-testid={`trust-${d.device_id}`}
                    title={d.is_trusted ? 'Remove trusted flag' : 'Mark as trusted'}
                  >
                    <Star className={`w-4 h-4 ${d.is_trusted ? 'fill-emerald-500 text-emerald-500' : ''}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busyId === d.device_id || d.is_current}
                    onClick={() => revoke(d)}
                    className="text-red-600 hover:bg-red-50"
                    data-testid={`revoke-${d.device_id}`}
                    title={d.is_current ? 'Cannot revoke current device' : 'Revoke device & sign out'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TrustedDevices;
